import { useState, useEffect, useCallback, useRef } from 'react';

export interface KeyboardVisibilityState {
  isKeyboardVisible: boolean;
  keyboardHeight: number;
}

/**
 * Hook to detect virtual keyboard visibility on mobile devices.
 *
 * Uses two strategies:
 * 1. VisualViewport API (preferred, more accurate)
 * 2. Fallback: viewport resize detection with height threshold
 *
 * @returns Object with isKeyboardVisible boolean and estimated keyboard height
 */
export function useKeyboardVisibility(): KeyboardVisibilityState {
  const [state, setState] = useState<KeyboardVisibilityState>({
    isKeyboardVisible: false,
    keyboardHeight: 0,
  });

  // Store initial viewport height for comparison
  const initialHeightRef = useRef<number>(typeof window !== 'undefined' ? window.innerHeight : 0);

  // VisualViewport API handler (preferred)
  const handleVisualViewportChange = useCallback(() => {
    if (!window.visualViewport) return;

    const viewportHeight = window.visualViewport.height;
    const windowHeight = window.innerHeight;

    // Keyboard is likely visible if visual viewport is significantly smaller
    // than window height (threshold: 150px to account for browser chrome)
    const heightDiff = windowHeight - viewportHeight;
    const isVisible = heightDiff > 150;

    setState({
      isKeyboardVisible: isVisible,
      keyboardHeight: isVisible ? heightDiff : 0,
    });
  }, []);

  // Fallback: window resize handler
  const handleResize = useCallback(() => {
    const currentHeight = window.innerHeight;
    const initialHeight = initialHeightRef.current;

    // Update initial height on first resize (handles orientation changes)
    if (currentHeight > initialHeight) {
      initialHeightRef.current = currentHeight;
      setState({ isKeyboardVisible: false, keyboardHeight: 0 });
      return;
    }

    // Keyboard is likely visible if height dropped by more than 150px
    const heightDiff = initialHeight - currentHeight;
    const isVisible = heightDiff > 150;

    setState({
      isKeyboardVisible: isVisible,
      keyboardHeight: isVisible ? heightDiff : 0,
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Prefer VisualViewport API if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      // Initial check
      handleVisualViewportChange();

      return () => {
        window.visualViewport?.removeEventListener('resize', handleVisualViewportChange);
      };
    }

    // Fallback to window resize
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleVisualViewportChange, handleResize]);

  return state;
}
